import React, { useMemo, useState } from "react";
import { Accordion, Button, Form, Spinner } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import SuccessButton from "../../organisms/SuccessButton";
import ServerValidationErrorAlert from "../../organisms/ServerValidationErrorAlert";
import { ValidationErrorResponse } from "../../../services/ValidationError";
import {
  GrupoUsuario,
  NuevoGrupoUsuario,
  QUERY_GRUPO_USUARIO_CONTIENE_USUARIOS,
  RolSeguridad,
  grupoContieneUsuarios,
} from "../../../services/seguridadServicios";
import { formatearRol } from "./formatearRol";

const CODIGO_GRUPO_REGEX = /^[\p{L}\p{N}_]+$/u;

const validarCodigoGrupo = (codigo: string) => {
  if (!codigo) {
    return "El código del grupo es obligatorio.";
  }

  if (!CODIGO_GRUPO_REGEX.test(codigo)) {
    return "El código solo puede contener letras, números o guiones bajos (_).";
  }

  return undefined;
};

function GrupoUsuarioAccordion({
  grupo,
  roles,
  codigoGrupoEsUnico,
  estaGuardando,
  estaEliminando,
  onGuardarGrupo,
  onEliminarGrupo,
}: {
  grupo: GrupoUsuario;
  roles: RolSeguridad[];
  codigoGrupoEsUnico: (grupoId: string, codigo: string) => boolean;
  estaGuardando: boolean;
  estaEliminando: boolean;
  onGuardarGrupo: (payload: {
    idGrupoUsuario: string;
    body: NuevoGrupoUsuario;
  }) => Promise<{ validationErrors?: ValidationErrorResponse }>;
  onEliminarGrupo: (idGrupoUsuario: string) => Promise<void>;
}) {
  const {
    data: contieneUsuarios,
    isLoading: contieneUsuariosIsLoading,
    isError: contieneUsuariosError,
  } = useQuery({
    queryKey: [QUERY_GRUPO_USUARIO_CONTIENE_USUARIOS, grupo.id],
    queryFn: () => grupoContieneUsuarios(grupo.id),
  });
  const [codigo, setCodigo] = useState(grupo.codigo);
  const [idsRolesSeleccionados, setIdsRolesSeleccionados] = useState(
    grupo.roles.map((rol) => rol.id)
  );
  const [errorCodigo, setErrorCodigo] = useState<string | undefined>(undefined);
  const [serverFailedValidations, setServerFailedValidations] = useState<
    ValidationErrorResponse | undefined
  >(undefined);

  const idsRolesIniciales = useMemo(() => {
    return grupo.roles.map((rol) => rol.id).sort();
  }, [grupo.roles]);

  const idsRolesOrdenados = useMemo(() => {
    return [...idsRolesSeleccionados].sort();
  }, [idsRolesSeleccionados]);

  const rolesOrdenados = useMemo(() => {
    return [...roles].sort((a, b) => {
      const catCmp = a.categoria.localeCompare(b.categoria);
      if (catCmp !== 0) return catCmp;
      return a.codigo.localeCompare(b.codigo);
    });
  }, [roles]);

  const hayCambiosRoles =
    JSON.stringify(idsRolesOrdenados) !== JSON.stringify(idsRolesIniciales);
  const hayCambiosCodigo = codigo !== grupo.codigo;
  const hayCambios = hayCambiosRoles || hayCambiosCodigo;

  const onSeleccionarTodosClick = () => {
    setIdsRolesSeleccionados(roles.map((rol) => rol.id));
  };

  const onLimpiarClick = () => {
    setIdsRolesSeleccionados([]);
  };

  const onGuardarClick = async () => {
    setServerFailedValidations(undefined);
    const errorValidacion = validarCodigoGrupo(codigo);
    if (errorValidacion) {
      setErrorCodigo(errorValidacion);
      return;
    }

    if (!codigoGrupoEsUnico(grupo.id, codigo)) {
      setErrorCodigo("Ya existe un grupo con ese código.");
      return;
    }

    setErrorCodigo(undefined);
    const result = await onGuardarGrupo({
      idGrupoUsuario: grupo.id,
      body: {
        codigo,
        roles: idsRolesSeleccionados.map((id) => ({ id })),
      },
    });

    if (result.validationErrors) {
      setServerFailedValidations(result.validationErrors);
    }
  };

  const ocultarEliminar = contieneUsuariosIsLoading || !!contieneUsuarios || estaGuardando;

  return (
    <Accordion.Item eventKey={grupo.codigo}>
      <Accordion.Header>{grupo.codigo}</Accordion.Header>
      <Accordion.Body>
        <>
          {serverFailedValidations && (
            <ServerValidationErrorAlert
              serverFailedValidations={serverFailedValidations}
              wrapperClass="mb-3"
            />
          )}

          <Form.Group className="mb-3">
            <Form.Label>Código del grupo</Form.Label>
            <Form.Control
              value={codigo}
              isInvalid={!!errorCodigo}
              disabled={estaGuardando || estaEliminando}
              onChange={(event) => {
                setCodigo(event.target.value);
                setErrorCodigo(undefined);
                setServerFailedValidations(undefined);
              }}
            />
            <Form.Control.Feedback type="invalid">{errorCodigo}</Form.Control.Feedback>
          </Form.Group>

          <section className="mb-3">
            <Form.Label className="mb-2">Roles</Form.Label>
            <div>
              {rolesOrdenados.map((rol) => {
                const estaSeleccionado = idsRolesSeleccionados.includes(rol.id);
                return (
                  <Form.Check
                    key={rol.id}
                    type="checkbox"
                    id={`grupo-${grupo.id}-rol-${rol.id}`}
                    label={`${formatearRol(rol.categoria)}: ${formatearRol(rol.codigo)}`}
                    checked={estaSeleccionado}
                    disabled={estaGuardando || estaEliminando}
                    title={rol.descripcion}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setIdsRolesSeleccionados((prev) => {
                        if (checked) {
                          return Array.from(new Set([...prev, rol.id]));
                        }
                        return prev.filter((id) => id !== rol.id);
                      });
                    }}
                  />
                );
              })}
            </div>
          </section>

          {contieneUsuarios && (
            <small className="text-muted d-block mb-2">
              Este grupo no se puede eliminar porque tiene usuarios asociados.
            </small>
          )}

          {contieneUsuariosError && (
            <small className="text-muted d-block mb-2">
              No fue posible verificar si este grupo tiene usuarios asociados.
            </small>
          )}

          <div className="d-flex gap-2 flex-wrap justify-content-center">
            <Button
              variant="secondary"
              size="sm"
              onClick={onLimpiarClick}
              disabled={estaGuardando || estaEliminando}
            >
              Limpiar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onSeleccionarTodosClick}
              disabled={estaGuardando || estaEliminando}
            >
              Seleccionar todos
            </Button>

            {hayCambios &&
              (estaGuardando ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <SuccessButton size="sm" onClick={onGuardarClick}>
                  Guardar
                </SuccessButton>
              ))}

            {!ocultarEliminar &&
              (estaEliminando ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onEliminarGrupo(grupo.id)}
                >
                  Eliminar
                </Button>
              ))}
          </div>
        </>
      </Accordion.Body>
    </Accordion.Item>
  );
}

export default GrupoUsuarioAccordion;
